import React, { useCallback, useState } from "react";
import {
  CommandBarButton,
  ContextualMenu,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IContextualMenuItem,
  PrimaryButton,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";
import Close from "@mui/icons-material/Close";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import ChatHistoryList from "./ChatHistoryList";
import { Conversation } from "../../api/models";
import styles from "./ChatHistory.module.css";
import { commandBarStyle } from "./types";

interface ChatHistoryPanelProps {
  // Panel state
  showContextualPopup: boolean;
  showHistoryPanel: boolean;
  onClosePanel: () => void;
  setShowContextualPopup: (show: boolean) => void;

  // Chat history data
  chatHistory: Conversation[];
  fetchingChatHistory: boolean;
  fetchingConvMessages: boolean;
  selectedConvId: string;

  // Chat history actions
  handleFetchHistory: () => Promise<void>;
  onSelectConversation: (id: string) => void;
  onHistoryTitleChange: (id: string, newTitle: string) => void;
  onHistoryDelete: (id: string) => void;
  onExport: (id: string, format: string) => Promise<void>;

  // Loading states
  isGenerating: boolean;
  isLoading: boolean;
  toggleToggleSpinner: (toggler: boolean) => void;

  // Clear all dialog state
  hideClearAllDialog: boolean;
  clearing: boolean;
  clearingError: boolean;
  onClearAllChatHistory: () => Promise<void>;
  onHideClearAllDialog: () => void;
}

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  showContextualPopup,
  showHistoryPanel,
  onClosePanel,
  setShowContextualPopup,
  chatHistory,
  fetchingChatHistory,
  fetchingConvMessages,
  selectedConvId,
  handleFetchHistory,
  onSelectConversation,
  onHistoryTitleChange,
  onHistoryDelete,
  onExport,
  isGenerating,
  isLoading,
  toggleToggleSpinner,
  hideClearAllDialog,
  clearing,
  clearingError,
  onClearAllChatHistory,
  onHideClearAllDialog,
}) => {
  if (!showHistoryPanel) return null;

  const [showContextualMenu, setShowContextualMenu] = useState(false);

  const onShowContextualMenu = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.preventDefault();
      setShowContextualMenu(true);
      setShowContextualPopup(true);
    },
    [],
  );

  const onHideContextualMenu = useCallback(
    () => setShowContextualMenu(false),
    [],
  );

  const menuItems: IContextualMenuItem[] = [
    {
      key: "clearAll",
      text: "Clear all chat history",
      disabled:
        !chatHistory.length ||
        isLoading ||
        fetchingConvMessages ||
        fetchingChatHistory,
      iconProps: {},
      onRenderIcon: () => <DeleteOutlined fontSize="small" />,
    },
  ];

  const clearAllDialogContentProps = {
    type: DialogType.close,
    title: !clearingError
      ? "Are you sure you want to clear all chat history?"
      : "Error deleting all of chat history",
    closeButtonAriaLabel: "Close",
    subText: !clearingError
      ? "All chat history will be permanently removed."
      : "Please try again. If the problem persists, please contact the site administrator.",
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  return (
    <section
      className={styles.historyContainer}
      data-is-scrollable
      aria-label="chat history panel"
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        wrap
        aria-label="chat history header"
        className="mt-8"
      >
        <StackItem>
          <Text
            role="heading"
            aria-level={2}
            style={{
              alignSelf: "center",
              fontWeight: "600",
              fontSize: "18px",
              marginRight: "auto",
              paddingLeft: "20px",
            }}
          >
            Chat history
          </Text>
        </StackItem>

        <Stack horizontal className={styles.historyPanelTopRightButtons}>
          <Stack horizontal>
            <CommandBarButton
              onRenderIcon={() => <MoreHoriz fontSize="small" />}
              title="Clear all chat history"
              onClick={onShowContextualMenu}
              aria-label="clear all chat history"
              styles={commandBarStyle}
              role="button"
              id="moreButton"
            />
            <ContextualMenu
              items={menuItems}
              hidden={!showContextualMenu}
              target="#moreButton"
              onItemClick={onHideClearAllDialog}
              onDismiss={onHideContextualMenu}
            />
          </Stack>

          <Stack horizontal>
            <CommandBarButton
              onRenderIcon={() => <Close fontSize="small" />}
              title="Hide"
              aria-label="hide button"
              role="button"
              onClick={onClosePanel}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack
        aria-label="chat history panel content"
        style={{
          display: "flex",
          height: "100%",
          padding: "1px",
        }}
      >
        <Stack className={styles.chatHistoryListContainer}>
          <ChatHistoryList
            fetchingChatHistory={fetchingChatHistory}
            handleFetchHistory={handleFetchHistory}
            chatHistory={chatHistory}
            onSelectConversation={onSelectConversation}
            selectedConvId={selectedConvId}
            onHistoryTitleChange={onHistoryTitleChange}
            onHistoryDelete={onHistoryDelete}
            onExport={onExport}
            isGenerating={isGenerating}
            toggleToggleSpinner={toggleToggleSpinner}
          />
        </Stack>
      </Stack>

      {showContextualPopup && (
        <Dialog
          hidden={hideClearAllDialog}
          onDismiss={clearing ? () => {} : onHideClearAllDialog}
          dialogContentProps={clearAllDialogContentProps}
          modalProps={modalProps}
        >
          <DialogFooter>
            {!clearingError && (
              <PrimaryButton
                onClick={onClearAllChatHistory}
                disabled={clearing}
                text="Clear All"
              />
            )}
            <DefaultButton
              onClick={onHideClearAllDialog}
              disabled={clearing}
              text={!clearingError ? "Cancel" : "Close"}
            />
          </DialogFooter>
        </Dialog>
      )}
    </section>
  );
};

export default ChatHistoryPanel;